import Vue from 'vue';
import Agents from '@/resources/agents';
import VisnetHubs from '@/resources/visnet-hubs';
import Devices from '@/resources/devices';
import Substations from '@/resources/substations';
import jsona from '@/lib/jsona';
import RootStateCommit from '@/store/mixins/rootstate-commit';

const MODULE_NAME = 'substationDevices';
const DEFAULT_STATE = {
  error: '',
  substation: null
};

const deleteSubstationVisnethub = (state, { externalVisnetHubId }) => {
  const visnetToRemove = state.substation.visnetHubs.findIndex(
    visnet => visnet.externalVisnetHubId === externalVisnetHubId
  );

  Vue.delete(state.substation.visnetHubs, visnetToRemove);
};

function findAgentIndex(state, codes) {
  return state.substation.agents.findIndex(agent => agent.code === codes.agentCode);
}

function changeAgentAndUpdateState(state, codes, attributes) {
  const agentIndex = findAgentIndex(state, codes);
  const agent = state.substation.agents[agentIndex];
  const newAgent = { ...agent, ...attributes };
  Vue.set(state.substation.agents, agentIndex, newAgent);
}

function changeDeviceAndUpdateState(state, codes, attributes) {
  const agentIndex = findAgentIndex(state, codes);
  const { devices } = state.substation.agents[agentIndex];
  const deviceIndex = devices.findIndex(device => {
    if (device['device-type'] !== codes.deviceType) {
      return false;
    }
    return device['modbus-address'] === codes.modbusAddress;
  });
  const device = state.substation.agents[agentIndex].devices[deviceIndex];
  const newDevice = { ...device, ...attributes };
  Vue.set(state.substation.agents[agentIndex].devices, deviceIndex, newDevice);
}

export default {
  namespaced: true,
  state: { ...DEFAULT_STATE },

  /* eslint no-param-reassign: ["error", { "props": false }] */
  mutations: {
    parseResponseData(state, data) {
      const { type, response } = data;

      Vue.set(state, type, jsona.deserialize(response));
    },
    resetStore(state) {
      Object.assign(state, DEFAULT_STATE);
    },
    removeAgent(state, codes) {
      changeAgentAndUpdateState(state, codes, { removed: true });
    },
    removeVisnethub(state, { substationCode, externalVisnetHubId }) {
      deleteSubstationVisnethub(state, { substationCode, externalVisnetHubId });
    },
    markAgentAsPermanentlyRemoved(state, codes) {
      changeAgentAndUpdateState(state, codes, { permanentlyRemoved: true });
    },
    rollbackRemoveAgent(state, codes) {
      changeAgentAndUpdateState(state, codes, { removed: false });
    },
    removeDevice(state, codes) {
      changeDeviceAndUpdateState(state, codes, { removed: true });
    },
    lowerVisnetCounter(state) {
      state.substation.devicesCounters.vh -= 1;
    },
    markDeviceAsPermanentlyRemoved(state, codes) {
      changeDeviceAndUpdateState(state, codes, { permanentlyRemoved: true });
    },
    rollbackRemoveDevice(state, codes) {
      changeDeviceAndUpdateState(state, codes, { removed: false });
    },
    setResponseError(state, error) {
      Object.assign(state.error, error);
    }
  },
  actions: {
    async fetchData({ commit, dispatch }, options) {
      RootStateCommit(commit, 'startLoading', MODULE_NAME);

      try {
        await Promise.all([dispatch('fetchSubstationDeviceData', options)]);

        RootStateCommit(commit, 'stopLoading', MODULE_NAME);
      } catch (error) {
        RootStateCommit(commit, 'stopLoading', MODULE_NAME);
      }
    },
    async fetchSubstationDeviceData({ commit }, options) {
      RootStateCommit(commit, 'startLoading', MODULE_NAME);

      const { projectCode, substationCode } = options;
      try {
        const response = await Substations({ projectCode }).get({
          code: substationCode,
          include: 'project,agents.devices,visnet_hubs,agents.diagrams,feeder_info_cards,guards'
        });

        if (!response) throw Error;

        await commit('parseResponseData', { type: 'substation', response: response.data });

        RootStateCommit(commit, 'stopLoading', MODULE_NAME);

        return true;
      } catch (error) {
        RootStateCommit(commit, 'stopLoading', MODULE_NAME);

        return false;
      }
    },
    removeAgent({ commit }, { projectCode, substationCode, agentCode }) {
      commit('removeAgent', { projectCode, substationCode, agentCode });
      Agents({ projectCode, substationCode })
        .delete({ code: agentCode })
        .then(
          () => commit('markAgentAsPermanentlyRemoved', { projectCode, substationCode, agentCode }),
          () => commit('rollbackRemoveAgent', { projectCode, substationCode, agentCode })
        );
    },
    removeDevice({ commit }, codes) {
      commit('removeDevice', codes);
      Devices.delete(codes).then(
        () => commit('markDeviceAsPermanentlyRemoved', codes),
        () => commit('rollbackRemoveDevice', codes)
      );
    },
    editVisnet({ commit }, { projectCode, substationCode, externalVisnetHubId, data }) {
      RootStateCommit(commit, 'startLoading', MODULE_NAME);

      VisnetHubs({ projectCode, substationCode })
        .update({ code: externalVisnetHubId }, { ...data })
        .then(
          () => {
            RootStateCommit(commit, 'stopLoading', MODULE_NAME);
          },
          () => {
            RootStateCommit(commit, 'setResponseError', MODULE_NAME);
          }
        );
    },
    removeVisnetHub({ commit }, { projectCode, substationCode, externalVisnetHubId }) {
      RootStateCommit(commit, 'startLoading', MODULE_NAME);

      VisnetHubs({ projectCode, substationCode })
        .delete({ code: externalVisnetHubId })
        .then(
          () => {
            commit('lowerVisnetCounter');
            commit('removeVisnethub', { substationCode, externalVisnetHubId });

            RootStateCommit(commit, 'stopLoading', MODULE_NAME);
          },
          () => {
            RootStateCommit(commit, 'setResponseError', MODULE_NAME);
          }
        );
    }
  },
  getters: {
    visnetHubs: state => !!state.substation && state.substation.visnetHubs
  }
};
